import { jsxs, jsx } from 'react/jsx-runtime';
import { useId, useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { CircleInfoIcon } from '../../../icons/CircleInfoIcon.js';
import { CircleExclamationIcon } from '../../../icons/CircleExclamationIcon.js';
import { CircleTickIcon } from '../../../icons/CircleTickIcon.js';
import { AUTOFILL_INPUT_CLASS, autofillOverrideStyle } from '../../../constants/autofillOverrideStyle.js';
import { useListboxDropdown } from '../../../hooks/useListboxDropdown/useListboxDropdown.js';
import { countryList } from './constants/CountryList.js';
import { computeMaskedInputChange, defaultNormalize } from './maskedInputChange.js';

const normalizeDialCode = (dialCode) => {
    if (!dialCode)
        return '';
    const hasPlus = dialCode.trim().startsWith('+');
    const digits = dialCode.replace(/\D/g, '');
    return hasPlus && digits.length > 0 ? `+${digits}` : digits;
};
const ChevronDown = ({ isOpen }) => (jsx("svg", { className: `transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`, width: "14", height: "8", viewBox: "0 0 14 8", fill: "none", xmlns: "http://www.w3.org/2000/svg", "aria-hidden": "true", focusable: "false", children: jsx("path", { d: "M6.90625 6.90625C6.71875 7.09375 6.375 7.09375 6.1875 6.90625L0.1875 0.90625C0 0.71875 0 0.375 0.1875 0.1875C0.375 0 0.71875 0 0.90625 0.1875L6.5625 5.84375L12.1875 0.1875C12.375 0 12.7188 0 12.9062 0.1875C13.0938 0.375 13.0938 0.71875 12.9062 0.90625L6.90625 6.90625Z", fill: "currentColor" }) }));
const styles = {
    container: 'w-full',
    label: 'block text-body-strong text-text-body mb-space-200',
    field: 'flex w-full h-12 rounded-radius-100 border border-input-border-default bg-input-background-default focus-within:border-border-focus-2 focus-within:border-3 overflow-hidden',
    fieldDisabled: 'bg-input-background-disabled border-input-border-disabled',
    fieldError: 'border-input-border-error',
    fieldSuccess: 'border-input-border-success',
    prefixBase: 'shrink-0 bg-transparent flex items-center gap-space-200 px-space-400 text-text-body',
    prefixButton: 'cursor-pointer rounded-radius-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-border-focus-1',
    prefixDisabled: 'cursor-not-allowed text-text-body',
    numberInputContainer: 'relative flex-1 min-w-0',
    numberInput: 'w-full h-full bg-transparent pr-space-400 py-space-200 outline-none disabled:cursor-not-allowed disabled:text-text-body',
    rightIconWrapper: 'absolute right-space-400 top-1/2 -translate-y-1/2 flex items-center pointer-events-none',
    dropdown: 'fixed py-space-200 px-space-200 bg-neutral-0 rounded-tenant shadow-[0_6px_16px_0px_rgba(0,0,0,0.12)] z-999',
    option: 'flex w-full items-center justify-between gap-space-300 rounded-tenant px-space-200 py-space-200 text-left focus:outline-none hover:bg-primary-100 focus:bg-primary-100',
    optionMeta: 'flex items-center gap-space-200 min-w-0',
    optionFlag: 'shrink-0',
    optionLabel: 'truncate min-w-0',
    hintTextContainer: 'mt-space-200 flex gap-space-100',
    hintText: 'text-body-small text-text-body',
    errorText: 'text-body-small text-text-error',
    successText: 'text-body-small text-text-success',
    hintIconContainer: 'flex items-center h-(--line-height-body-small)',
};
const PhoneInput = ({ id, name, value, defaultValue, placeholder, disabled = false, required = false, readOnly = false, autoComplete, rightIcon, label, hideLabel = false, hintText, hasError, hasSuccess, onChange, onBlur, onFocus, ref, options, selectedCode, defaultSelectedCode, onCodeChange, ...rest }) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const hintId = `${inputId}-hint`;
    const errorId = `${inputId}-error`;
    const successId = `${inputId}-success`;
    const listboxId = `${inputId}-country-listbox`;
    const labelId = `${inputId}-label`;
    const buttonId = `${inputId}-country-button`;
    const effectiveOptions = options && options.length > 0 ? options : countryList;
    const isControlled = selectedCode !== undefined;
    const [internalSelectedCode, setInternalSelectedCode] = useState(defaultSelectedCode ?? effectiveOptions[0]?.value ?? '');
    const numberInputRef = useRef(null);
    const rightIconRef = useRef(null);
    // Tracks the number input's own last-known formatted text purely for
    // caret math (see onChange below). This is not mask logic and carries no
    // notion of "how" a change happened — it's just "what did we show last".
    const lastFormattedValueRef = useRef('');
    useEffect(() => {
        if (isControlled)
            return;
        const hasCurrentOption = effectiveOptions.some((option) => option.value === internalSelectedCode);
        if (hasCurrentOption)
            return;
        setInternalSelectedCode(defaultSelectedCode ?? effectiveOptions[0]?.value ?? '');
    }, [defaultSelectedCode, effectiveOptions, internalSelectedCode, isControlled]);
    const resolvedSelectedCode = isControlled ? selectedCode : internalSelectedCode;
    const selectedOption = effectiveOptions.find((option) => option.value === resolvedSelectedCode) ?? effectiveOptions[0];
    const isSelectable = effectiveOptions.length > 1;
    const isPrefixInteractive = isSelectable && !disabled && !readOnly;
    const { isOpen, activeIndex, containerRef, triggerRef, listRef, toggle, selectIndex, handleTriggerKeyDown, handleOptionKeyDown, registerOptionRef, } = useListboxDropdown({
        itemCount: effectiveOptions.length,
        selectedIndex: effectiveOptions.findIndex((option) => option.value === resolvedSelectedCode),
        getItemText: (index) => effectiveOptions[index]?.label ?? effectiveOptions[index]?.dialCode ?? '',
        onSelect: (index) => {
            const option = effectiveOptions[index];
            if (option)
                handleSelect(option.value);
        },
    });
    const updatePosition = useCallback(() => {
        if (!triggerRef.current || !listRef.current || !containerRef.current)
            return;
        const buttonRect = triggerRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        const list = listRef.current;
        // Size to content by default, but keep it at least trigger-width and at most field-width.
        list.style.width = 'max-content';
        list.style.minWidth = `${buttonRect.width}px`;
        list.style.maxWidth = `${containerRect.width}px`;
        list.style.top = `${buttonRect.bottom + 4}px`;
        list.style.left = `${buttonRect.left}px`;
    }, [containerRef, listRef, triggerRef]);
    useLayoutEffect(() => {
        const list = listRef.current;
        if (!list || !isSelectable)
            return;
        if (isOpen) {
            updatePosition();
            list.style.display = '';
        }
        else {
            list.style.display = 'none';
        }
    }, [isOpen, isSelectable, listRef, updatePosition]);
    useLayoutEffect(() => {
        const inputElement = numberInputRef.current;
        if (!inputElement)
            return;
        const rightIconWidth = rightIconRef.current?.firstElementChild?.getBoundingClientRect().width ||
            rightIconRef.current?.offsetWidth ||
            0;
        const defaultPadding = 16;
        const rightPadding = rightIconWidth ? rightIconWidth + defaultPadding * 1.5 : defaultPadding;
        inputElement.style.paddingRight = `${rightPadding}px`;
    }, [rightIcon]);
    useEffect(() => {
        if (!isOpen || !isSelectable)
            return;
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, isSelectable, updatePosition]);
    const describedBy = [
        hintText && !hasError && !hasSuccess && hintId,
        !!hasError && errorId,
        !!hasSuccess && !hasError && successId,
    ]
        .filter(Boolean)
        .join(' ');
    const formatNumberPart = (option, rawValue) => {
        return option?.mask?.format ? option.mask.format(rawValue) : rawValue;
    };
    const normalizeNumberPart = (option, displayValue) => {
        return option?.mask?.normalize
            ? option.mask.normalize(displayValue)
            : defaultNormalize(displayValue);
    };
    const getLocalNumberPart = (option, rawValue) => {
        const normalizedValue = defaultNormalize(rawValue);
        const dialCodeDigits = defaultNormalize(option?.dialCode ?? '');
        if (!normalizedValue || !dialCodeDigits) {
            return normalizedValue;
        }
        if (rawValue.trim().startsWith('+') && normalizedValue.startsWith(dialCodeDigits)) {
            return normalizedValue.slice(dialCodeDigits.length);
        }
        return normalizedValue;
    };
    const buildFullPhoneNumber = (option, dialCode, numberPart) => {
        const code = normalizeDialCode(dialCode);
        return `${code}${normalizeNumberPart(option, numberPart)}`;
    };
    const emitChange = (option, numberPart) => {
        if (!onChange)
            return;
        const fullPhoneNumber = buildFullPhoneNumber(option, option?.dialCode, numberPart);
        onChange({
            target: {
                name: name ?? '',
                value: fullPhoneNumber,
            },
            currentTarget: {
                name: name ?? '',
                value: fullPhoneNumber,
            },
        });
    };
    const getCurrentNumberPart = (option) => {
        if (value !== undefined && value !== null) {
            return formatNumberPart(option, getLocalNumberPart(option, String(value)));
        }
        return formatNumberPart(option, normalizeNumberPart(option, numberInputRef.current?.value ?? ''));
    };
    const handleSelect = (optionValue) => {
        const option = effectiveOptions.find((entry) => entry.value === optionValue);
        const nextOption = option ?? selectedOption;
        if (!isControlled) {
            setInternalSelectedCode(optionValue);
        }
        onCodeChange?.(optionValue);
        emitChange(nextOption, getCurrentNumberPart(nextOption));
    };
    const fieldClassName = [
        styles.field,
        disabled ? styles.fieldDisabled : '',
        hasError ? styles.fieldError : '',
        hasSuccess && !hasError ? styles.fieldSuccess : '',
    ]
        .filter(Boolean)
        .join(' ');
    const prefixClassName = [
        styles.prefixBase,
        isPrefixInteractive ? styles.prefixButton : '',
        disabled ? styles.prefixDisabled : '',
    ]
        .filter(Boolean)
        .join(' ');
    const inputValue = value !== undefined && value !== null
        ? formatNumberPart(selectedOption, getLocalNumberPart(selectedOption, String(value)))
        : undefined;
    const inputDefaultValue = defaultValue !== undefined && defaultValue !== null
        ? formatNumberPart(selectedOption, getLocalNumberPart(selectedOption, String(defaultValue)))
        : undefined;
    useEffect(() => {
        if (inputValue !== undefined) {
            lastFormattedValueRef.current = inputValue;
        }
    });
    return (jsxs("div", { ref: containerRef, className: styles.container, children: [label && (jsx("label", { id: labelId, htmlFor: inputId, className: `${styles.label} ${hideLabel ? 'sr-only' : ''}`, children: label })), jsxs("div", { className: fieldClassName, children: [isSelectable ? (jsxs("button", { id: buttonId, ref: triggerRef, type: "button", className: prefixClassName, onClick: () => {
                            if (isPrefixInteractive) {
                                toggle();
                            }
                        }, onKeyDown: handleTriggerKeyDown, "aria-expanded": isPrefixInteractive ? isOpen : undefined, "aria-haspopup": isPrefixInteractive ? 'listbox' : undefined, "aria-controls": isPrefixInteractive ? listboxId : undefined, "aria-label": selectedOption
                            ? `Select country code, current ${selectedOption.label ?? selectedOption.dialCode}`
                            : 'Select country code', disabled: !isPrefixInteractive, children: [jsx("span", { "aria-hidden": "true", children: selectedOption?.icon }), jsx("span", { children: selectedOption?.dialCode }), jsx(ChevronDown, { isOpen: isOpen })] })) : (jsxs("div", { className: prefixClassName, children: [jsx("span", { "aria-hidden": "true", children: selectedOption?.icon }), jsx("span", { children: selectedOption?.dialCode })] })), jsxs("div", { className: styles.numberInputContainer, children: [jsx("input", { ref: (node) => {
                                    numberInputRef.current = node;
                                    if (typeof ref === 'function') {
                                        ref(node);
                                    }
                                    else if (ref) {
                                        ref.current = node;
                                    }
                                }, id: inputId, name: name, type: "tel", value: inputValue, defaultValue: inputDefaultValue, placeholder: placeholder, disabled: disabled, required: required, readOnly: readOnly, autoComplete: autoComplete, onChange: (event) => {
                                    const inputEl = event.target;
                                    const { digits, formattedValue, caretPosition } = computeMaskedInputChange({
                                        mask: selectedOption?.mask,
                                        rawValue: inputEl.value,
                                        caretPosition: inputEl.selectionStart ?? inputEl.value.length,
                                        previousFormattedValue: lastFormattedValueRef.current,
                                    });
                                    lastFormattedValueRef.current = formattedValue;
                                    if (formattedValue !== inputEl.value) {
                                        inputEl.value = formattedValue;
                                        inputEl.setSelectionRange(caretPosition, caretPosition);
                                    }
                                    emitChange(selectedOption, digits);
                                }, onBlur: onBlur, onFocus: onFocus, "aria-invalid": !!hasError, "aria-describedby": describedBy, className: `${styles.numberInput} ${AUTOFILL_INPUT_CLASS}`, ...rest }), jsx("style", { children: autofillOverrideStyle }), rightIcon && (jsx("span", { ref: rightIconRef, className: styles.rightIconWrapper, children: rightIcon }))] })] }), isSelectable &&
                typeof document !== 'undefined' &&
                createPortal(jsx("ul", { id: listboxId, ref: listRef, className: styles.dropdown, style: { display: 'none' }, role: "listbox", "aria-label": "Country code", children: effectiveOptions.map((option, index) => (jsxs("li", { ref: registerOptionRef(index), role: "option", tabIndex: index === activeIndex ? 0 : -1, "aria-selected": selectedOption?.value === option.value, className: styles.option, onClick: () => selectIndex(index), onKeyDown: (event) => handleOptionKeyDown(event, index), children: [jsxs("span", { className: styles.optionMeta, children: [jsx("span", { className: styles.optionFlag, "aria-hidden": "true", children: option.icon }), jsx("span", { className: styles.optionLabel, children: option.label ?? option.dialCode })] }), jsx("span", { children: option.dialCode })] }, option.value))) }), document.body), hintText &&
                (!hasError || typeof hasError !== 'string') &&
                (!hasSuccess || typeof hasSuccess !== 'string') && (jsxs("div", { className: styles.hintTextContainer, children: [jsx("div", { className: styles.hintIconContainer, children: jsx(CircleInfoIcon, {}) }), jsx("p", { id: hintId, className: styles.hintText, children: hintText })] })), hasError && typeof hasError === 'string' && (jsxs("div", { className: `${styles.hintTextContainer} text-text-error`, children: [jsx("div", { className: styles.hintIconContainer, children: jsx(CircleExclamationIcon, {}) }), jsx("p", { id: errorId, className: styles.errorText, role: "alert", children: hasError })] })), hasSuccess && !hasError && typeof hasSuccess === 'string' && (jsxs("div", { className: `${styles.hintTextContainer} text-text-success`, children: [jsx("div", { className: styles.hintIconContainer, children: jsx(CircleTickIcon, {}) }), jsx("p", { id: successId, className: styles.successText, children: hasSuccess })] }))] }));
};

export { PhoneInput };
